/**
 * @fileoverview The pictures at the top of a plant card, swiped through.
 *
 * One four-by-three frame, whatever each photograph's own shape, because a
 * grid of cards that each set their own height is not a grid. Inside it the
 * photos sit side by side, each a little narrower than the frame, so the
 * edge of the next one shows and asks to be swiped to. Letting go snaps to
 * a whole picture. Arrows on the picture's sides step to the one before and
 * after (owner, 2026-10-14: "arrows on the images instead of dots"; the row of
 * dots under the picture also made a card with several photos taller than one
 * with a single photo), and the caption counts where you are ("2 / 8"); an
 * arrow and a swipe move the same selection.
 *
 * A finger swipes through the native scroller. A mouse cannot, so it drags:
 * the same motion, done by hand. And a tap or click on the picture in view
 * opens it for editing — the picture is the biggest thing on the card, and
 * it used to do nothing when touched.
 *
 * Over the foot of each picture: the day (none for a bed's dateless cover), and whatever the gardener wrote
 * about it, then, on a photo Grow filed itself (B160, the server's `auto_filed_at`), "Filed by Grow · Move" (FiledByGrowLine; Move opens the one
 * move sheet, MovePhotoSheet). Whether a photo shows on the public page is the card's eye
 * button (PlantCard), not a mark on the picture. (Photos show no stage since stages left photos; plantStage.)
 *
 * Where: the plant card (PlantCard) and the bed card (LocationCard), as the
 * picture at the top; their on-picture buttons (the photo's tools, the
 * arrows) are the shared PhotoButton (ui/PhotoButton).
 * API: none; the photos come in.
 * Settings: PHOTO_DRAG_SETTLE_MS, PHOTO_DRAG_THRESHOLD_PX,
 * PHOTO_PEEK_SLIDE_WIDTH, PHOTO_SLIDE_GAP_PX, PHOTO_TAP_SETTLE_MS
 * (config/tunables/plantList).
 * Words (en/es): tray.filed.byGrow, plants.card.photos.carousel, .slide, .edit, .loading, .alt, .hiddenBadge, .prev, .next, .jump.
 *
 * @module PlantCardPhoto
 */

import React from 'react';
import { ChevronLeft, ChevronRight, ImageIcon, Pencil } from 'lucide-react';
import { FramedPhoto } from '@/shared/components/ui/FramedPhoto';
import Spinner from '@/shared/components/ui/Spinner';
import { PhotoButton, PhotoMark } from '@/shared/components/ui/PhotoButton';
import { FiledByGrowLine } from '@/shared/components/ui/FiledByGrowLine';
import { askMovePhoto } from '@/shared/lib/movePhoto';
import { PlantPhoto } from '@/shared/types/photo';
import { formatDateLong, calendarDay } from '@/shared/utils/dateUtils';
import { brandConfig } from '@/shared/styles/base';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { cn } from '@/lib/utils';
import { photoUrl } from '../../lib/plantPhotoUrl';
import {
  PHOTO_DRAG_SETTLE_MS, PHOTO_DRAG_THRESHOLD_PX, PHOTO_PEEK_SLIDE_WIDTH, PHOTO_SLIDE_GAP_PX, PHOTO_TAP_SETTLE_MS,
} from '@/config/tunables/plantList';

/** The card's photographs, which one is in view, and what the card does with them. */
interface PlantCardPhotoProps {
  /** The card's photographs, newest first. */
  photos: PlantPhoto[];
  /** Index of the picture in view. */
  selected: number;
  /** Called with the index a swipe or drag settles on. */
  onSelect: (index: number) => void;
  /** True while the picture in view is still arriving. */
  isLoading: boolean;
  /** What the gardener wrote about a picture, if anything. */
  noteFor: (photo: PlantPhoto) => string | null;
  /** Used for the alt text. */
  plantName: string;
  /** Omitted where the photograph cannot be changed, as on an archived plant. */
  onEditPhoto?: () => void;
  /**
   * Whether to draw the pencil on the picture that says it can be tapped.
   * The plant card leaves it off: its photo tools (actions) carry their own
   * pencil.
   */
  editHint?: boolean;
  /**
   * The photo's own tools, drawn on the picture at its top right, as photo
   * apps put them: round see-through buttons (PhotoButton). They sat in a
   * row under the picture, which looked like a toolbar with nothing to
   * belong to.
   */
  actions?: React.ReactNode;
  /** False shows each picture whole; true lets the next one show at the edge. */
  peek?: boolean;
  /** Called when a picture fails to load; the bed card uses it to fall back to its first-photo invitation (B158 job 8). */
  onImageError?: () => void;
  /**
   * The plant these photos are on (B160). With it, a photo the server marks `auto_filed_at` (filed by Grow itself, shown only
   * while the server still counts it as new) carries "Filed by Grow · Move" under its date and note, and Move opens the one move
   * sheet. Omitted on the bed card and on an archived plant: no line.
   */
  plantObjId?: number;
}

/**
 * A card's photographs as a swipeable strip, one whole picture at a time,
 * with its caption. The corner stays clear: no weather marks, no count.
 *
 * @param props - PlantCardPhotoProps.
 */
export const PlantCardPhoto: React.FC<PlantCardPhotoProps> = ({
  photos, selected, onSelect, isLoading, noteFor, plantName, onEditPhoto, editHint = true, actions, peek = true, plantObjId, onImageError,
}) => {
  const { reactTranslate: t } = useTranslation();
  /** The sideways-scrolling strip of slides. */
  const scrollerRef = React.useRef<HTMLDivElement>(null);
  const many = photos.length > 1;

  /**
   * A tap on a dot brings that picture here; a swipe that settles on a
   * picture lights its dot. The two must not feed each other: the scroll a
   * tap starts fires scroll events from the first frame, before anything has
   * moved, and read naively those said "still on the old one" and flipped
   * the selection straight back. So while a tap's scroll is settling, scroll
   * events are ignored until it lands — or, if the snap lands it elsewhere,
   * until a short grace runs out.
   */
  const settling = React.useRef<number | null>(null);
  /** The grace after which a settling scroll stops being waited for. */
  const settleTimer = React.useRef<number | undefined>(undefined);
  /**
   * Turns snapping back on after a mouse drag. Its own timer, because a
   * settling scroll that lands clears settleTimer, and snapping used to stay
   * off for good after the first drag.
   */
  const snapTimer = React.useRef<number | undefined>(undefined);
  /** How far apart two pictures sit in the strip, in pixels: a slide's width and the gap after it. */
  const slideStep = () => {
    const first = scrollerRef.current?.children[0] as HTMLElement | undefined;
    return first ? first.offsetWidth + (peek ? PHOTO_SLIDE_GAP_PX : 0) : 1;
  };

  /**
   * Before paint, and straight there when the pictures themselves moved.
   *
   * When the list is re-sorted — a photo added, or its date changed, lands
   * where its day puts it — the strip has to be on the new position before
   * the browser gets a chance to report a scroll from the old one, or that
   * report reads as the gardener swiping back and the card shows the newest
   * picture instead of the one just added. A tap on a dot still glides.
   */
  const lastPhotos = React.useRef(photos);
  React.useLayoutEffect(() => {
    const reordered = lastPhotos.current !== photos;
    lastPhotos.current = photos;
    const scroller = scrollerRef.current;
    if (!scroller || !many) return;
    const slide = scroller.children[selected] as HTMLElement | undefined;
    if (!slide) return;
    if (Math.round(scroller.scrollLeft / slideStep()) === selected) return;
    settling.current = selected;
    window.clearTimeout(settleTimer.current);
    settleTimer.current = window.setTimeout(() => { settling.current = null; }, PHOTO_TAP_SETTLE_MS);
    scroller.scrollTo({ left: slide.offsetLeft, behavior: reordered ? 'auto' : 'smooth' });
    // slideStep reads the DOM at call time; it is not state to depend on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, many, peek, photos]);

  React.useEffect(() => () => {
    window.clearTimeout(settleTimer.current);
    window.clearTimeout(snapTimer.current);
  }, []);

  /** Lights the dot of the picture a swipe has settled on, unless a tap's own scroll is still landing. */
  const onScroll = () => {
    const scroller = scrollerRef.current;
    if (!scroller || !many) return;
    const index = Math.round(scroller.scrollLeft / slideStep());
    if (settling.current !== null) {
      if (index === settling.current) { settling.current = null; window.clearTimeout(settleTimer.current); }
      return;
    }
    if (index !== selected && index >= 0 && index < photos.length) onSelect(index);
  };

  /**
   * Dragging, for a mouse.
   *
   * A finger swipes a scroll-snap strip natively; a mouse just sits on it.
   * So a mouse press takes the strip by hand: snapping is switched off for
   * the duration, or the browser would fight every pixel of the drag, and
   * when the button comes up the strip is sent to the nearest whole picture
   * and snapping switched back on once it has arrived. A press that moved
   * is not a click, so the click that follows it is swallowed.
   *
   * The strip takes hold of the pointer only once the press has become a
   * drag. It used to take hold on the press itself, and a held pointer sends
   * its click to the strip rather than the picture under it, so on any card
   * with more than one photo a click on the picture never reached its editor
   * and only the pencil opened it.
   */
  const drag = React.useRef<{ x: number; left: number; moved: boolean } | null>(null);
  /** True for the moment after a drag, so the click it ends with does not open the editor. */
  const swallowClick = React.useRef(false);

  /** Notes where a mouse press began, in case it turns into a drag. */
  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'mouse' || event.button !== 0 || !many) return;
    const scroller = scrollerRef.current;
    if (!scroller) return;
    drag.current = { x: event.clientX, left: scroller.scrollLeft, moved: false };
  };

  /**
   * Moves the strip with the mouse once the press has gone far enough to be
   * a drag, taking hold of the pointer from then on so the drag survives
   * leaving the frame.
   */
  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const scroller = scrollerRef.current;
    if (!drag.current || !scroller) return;
    // The button came up somewhere this strip was not listening.
    if (event.buttons === 0) { drag.current = null; return; }
    const dx = event.clientX - drag.current.x;
    if (!drag.current.moved) {
      if (Math.abs(dx) <= PHOTO_DRAG_THRESHOLD_PX) return;
      drag.current.moved = true;
      scroller.style.scrollSnapType = 'none';
      scroller.setPointerCapture(event.pointerId);
    }
    scroller.scrollLeft = drag.current.left - dx;
  };

  /**
   * Ends a press. One that never moved is left to be a click; one that
   * dragged is sent to the nearest whole picture, and its click swallowed.
   */
  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    const scroller = scrollerRef.current;
    if (!drag.current || !scroller) return;
    const { moved } = drag.current;
    drag.current = null;
    if (scroller.hasPointerCapture(event.pointerId)) scroller.releasePointerCapture(event.pointerId);

    if (!moved) return;
    swallowClick.current = true;
    window.setTimeout(() => { swallowClick.current = false; }, 0);

    const index = Math.max(0, Math.min(photos.length - 1, Math.round(scroller.scrollLeft / slideStep())));
    const slide = scroller.children[index] as HTMLElement | undefined;
    settling.current = index;
    window.clearTimeout(settleTimer.current);
    settleTimer.current = window.setTimeout(() => { settling.current = null; }, PHOTO_DRAG_SETTLE_MS);
    window.clearTimeout(snapTimer.current);
    snapTimer.current = window.setTimeout(() => { scroller.style.scrollSnapType = ''; }, PHOTO_DRAG_SETTLE_MS);
    if (slide) scroller.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
    if (index !== selected) onSelect(index);
  };

  /** Opens the picture in view for editing, unless the press was a drag. */
  const openEditor = () => {
    if (swallowClick.current || !onEditPhoto) return;
    onEditPhoto();
  };

  if (photos.length === 0) {
    /*
     * Only reachable while a picture is between states — uploaded but not
     * yet chosen, or deleted a moment ago. A plant that simply has no
     * photographs never renders this component at all; it gets the one-row
     * invitation instead.
     */
    return (
      <div className="relative flex aspect-[4/3] w-full items-center justify-center bg-muted">
        <brandConfig.emptyStateIcon
          className={`${brandConfig.iconSizes.lg} text-muted-foreground`}
          aria-hidden="true"
        />
      </div>
    );
  }

  return (
    <div className="relative aspect-[4/3] w-full overflow-hidden">
      <div
        ref={scrollerRef}
        onScroll={onScroll}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        className={cn(
          'flex h-full snap-x snap-mandatory overflow-x-auto overflow-y-hidden scrollbar-hide',
          many && peek && 'gap-2',
          many && 'cursor-grab active:cursor-grabbing',
        )}
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' } as React.CSSProperties}
        aria-roledescription={t('plants.card.photos.carousel')}
        aria-label={plantName}
      >
        {photos.map((photo, index) => {
          const note = noteFor(photo);
          const inView = index === selected;
          const editable = inView && Boolean(onEditPhoto);
          return (
            <div
              key={photo.photo_id}
              className={cn(
                'group relative h-full shrink-0 snap-start overflow-hidden',
                editable && 'cursor-pointer',
              )}
              style={{ width: many && peek ? PHOTO_PEEK_SLIDE_WIDTH : '100%' }}
              aria-roledescription={t('plants.card.photos.slide')}
              aria-label={`${index + 1} / ${photos.length}`}
              /* The picture in view is the way into its editor. It is a div
                 rather than a button because a button cannot hold the caption
                 block, and the keyboard is served by hand. */
              role={editable ? 'button' : undefined}
              tabIndex={editable ? 0 : undefined}
              title={editable ? t('plants.card.photos.edit') : undefined}
              onClick={editable ? openEditor : undefined}
              onKeyDown={editable ? (event) => {
                // A key pressed on a button inside the slide (Move) is that button's, not the editor's.
                if (event.target !== event.currentTarget) return;
                if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); openEditor(); }
              } : undefined}
            >
              {inView && isLoading && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-muted/90">
                  <Spinner size={48} color="hsl(var(--primary))" />
                  <span className="mt-2 text-sm text-muted-foreground">{t('plants.card.photos.loading')}</span>
                </div>
              )}
              <FramedPhoto
                src={photoUrl(photo, 'card')}
                alt={t('plants.card.photos.alt', { name: plantName })}
                loading={Math.abs(index - selected) <= 1 ? 'eager' : 'lazy'}
                draggable={false}
                onError={onImageError}
              />
              {/* A hint, not a button: the whole picture is the target and
                  this lets clicks through. Without it nothing on the picture
                  said it could be tapped, and on a phone people tap a
                  photograph expecting it to zoom. */}
              {editable && editHint && (
                <PhotoMark tone="light" className="absolute left-2 top-2 z-10">
                  <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                </PhotoMark>
              )}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent px-2 pb-1.5 pt-4 text-white">
                <div className={cn('flex items-center gap-2', many ? 'justify-between' : 'justify-end')}>
                  {/* Where this one is among them, now the dots are gone. */}
                  {many && (
                    <span className="text-xs font-medium leading-snug opacity-90" aria-label={t('plants.card.photos.jump', { n: index + 1, total: photos.length })}>
                      {index + 1} / {photos.length}
                    </span>
                  )}
                  {/* A photo with no date (a bed's cover) draws none (B158 job 8). */}
                  {photo.date_taken && (
                    <span className="text-xs font-medium leading-snug opacity-90">
                      {formatDateLong(calendarDay(photo.date_taken))}
                    </span>
                  )}
                </div>
                {note && (
                  <p className="mt-0.5 flex items-start justify-end gap-1 text-right text-sm leading-snug">
                    <ImageIcon className="mt-0.5 h-3 w-3 shrink-0 opacity-80" aria-hidden="true" />
                    {/* The gardener's own words, in quotes, so they do not read as a label. */}
                    <span>“{note}”</span>
                  </p>
                )}
                {/* Filed by Grow itself (B160): the line under the date and note, with the way to move it. A press on it is its own, not the drag's. */}
                {plantObjId !== undefined && photo.auto_filed_at && (
                  <div className="flex justify-end" onPointerDown={(event) => event.stopPropagation()}>
                    <FiledByGrowLine
                      tone="onPhoto"
                      className="w-auto"
                      text={t('tray.filed.byGrow')}
                      onMove={() => askMovePhoto({ plantObjId, photoIds: [photo.photo_id] })}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Over the carousel, not in a slide, so they stay put as it moves;
          a press on them is theirs, not the carousel's drag or the picture's
          tap. */}
      {actions && (
        <div
          className="absolute right-2 top-2 z-20 flex gap-4"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          {actions}
        </div>
      )}

      {/* The one before and after, on the picture's sides; none past either end. A press is theirs, not the drag's or the editor's. */}
      {many && selected > 0 && (
        <PhotoButton
          size="md"
          label={t('plants.card.photos.prev')}
          onClick={() => onSelect(selected - 1)}
          className="absolute left-2 top-1/2 z-20 -translate-y-1/2"
        >
          <ChevronLeft className="h-5 w-5" aria-hidden="true" />
        </PhotoButton>
      )}
      {many && selected < photos.length - 1 && (
        <PhotoButton
          size="md"
          label={t('plants.card.photos.next')}
          onClick={() => onSelect(selected + 1)}
          className="absolute right-2 top-1/2 z-20 -translate-y-1/2"
        >
          <ChevronRight className="h-5 w-5" aria-hidden="true" />
        </PhotoButton>
      )}
    </div>
  );
};
